/**
 * useSubscription — React Query hooks for all subscription operations.
 *
 * REPLACES context/SubscriptionContext.tsx + stores/subscriptionStore.ts.
 * The legacy stack used a Context that subscribed to a Zustand store
 * that called the (broken) class-based service. Two state layers and
 * a stale fetch flag (hasFetchedOnce) that caused the auth-gated loop.
 *
 * New design:
 *   - useCurrentSubscription / useSubscriptionPlans / useBillingHistory /
 *     useUsageAnalytics are read hooks. They use React Query's built-in
 *     auth gating via `enabled` rather than tracking fetch flags.
 *   - useUpgradeSubscription / useCancelSubscription / useReactivateSubscription /
 *     useRenewSubscription / useUpdateAutoRenew / useUpdatePaymentMethod
 *     are mutation hooks. They invalidate the relevant queries on success
 *     so the UI re-renders with fresh data automatically.
 *
 * Auth gating: hooks accept `enabled` from the caller (the Subscriptions
 * page wraps itself in RequireAdmin / RequireTenantOwner so the hook
 * will only fire on a properly-authed mount). No global side-effects.
 *
 * Tip for consumers: status decisions go in component code, not the
 * hook. The hook returns raw Subscription data and the caller decides
 * what UI to show for each of the 6 status values.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import subscriptionService from "@/services/subscriptionService";
import type {
  Subscription,
  SubscriptionPlan,
  BillingHistoryResponse,
  UsageAnalytics,
  UpgradeRequest,
  UpgradeResponse,
  PaymentMethodUpdate,
} from "@/types/subscription.types";

// ─── Query keys ───────────────────────────────────────────────────

export const subscriptionKeys = {
  all: ["subscription"] as const,
  current: () => [...subscriptionKeys.all, "current"] as const,
  plans: () => [...subscriptionKeys.all, "plans"] as const,
  billing: (page: number, limit: number) =>
    [...subscriptionKeys.all, "billing", page, limit] as const,
  usage: (startDate?: string, endDate?: string) =>
    [...subscriptionKeys.all, "usage", startDate, endDate] as const,
  byId: (id: string) => [...subscriptionKeys.all, "byId", id] as const,
};

// ─── Read hooks ───────────────────────────────────────────────────

/**
 * Current subscription. Refetches on window focus by default; can be
 * disabled by passing { enabled: false } (used during auth init).
 */
export function useCurrentSubscription(
  options?: Omit<UseQueryOptions<Subscription, Error>, "queryKey" | "queryFn">,
) {
  return useQuery<Subscription, Error>({
    queryKey: subscriptionKeys.current(),
    queryFn: () => subscriptionService.getCurrent(),
    staleTime: 30_000,
    // 404 = no subscription. Don't retry that — it's a valid response state.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return false;
      return failureCount < 1;
    },
    ...options,
  });
}

/**
 * All available subscription plans. Plans are basically static so we
 * cache aggressively.
 */
export function useSubscriptionPlans(
  options?: Omit<
    UseQueryOptions<SubscriptionPlan[], Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<SubscriptionPlan[], Error>({
    queryKey: subscriptionKeys.plans(),
    queryFn: () => subscriptionService.getPlans(),
    staleTime: 5 * 60 * 1000, // 5 minutes — plans rarely change
    ...options,
  });
}

/**
 * Billing history with pagination. Each (page, limit) combo gets its
 * own cache entry so paging back to a previous page is instant.
 */
export function useBillingHistory(page = 1, limit = 10) {
  return useQuery<BillingHistoryResponse, Error>({
    queryKey: subscriptionKeys.billing(page, limit),
    queryFn: () => subscriptionService.getBillingHistory(page, limit),
    staleTime: 60_000,
  });
}

/**
 * Usage analytics for the current period. Pass date strings to scope
 * a different window.
 */
export function useUsageAnalytics(startDate?: string, endDate?: string) {
  return useQuery<UsageAnalytics, Error>({
    queryKey: subscriptionKeys.usage(startDate, endDate),
    queryFn: () => subscriptionService.getUsageAnalytics(startDate, endDate),
    staleTime: 60_000,
  });
}

// ─── Mutation hooks ───────────────────────────────────────────────

/**
 * Initiate an upgrade. Backend returns either a payment-pending shape
 * (with authorizationUrl) or completed. The caller is responsible for
 * redirecting on payment_pending — we just bubble the result up.
 *
 * On success we invalidate the current subscription so the UI reflects
 * the new state immediately. For payment-pending flows the actual plan
 * change happens on webhook delivery; we'll refetch then too via the
 * payment-callback page.
 */
export function useUpgradeSubscription() {
  const qc = useQueryClient();
  return useMutation<UpgradeResponse, Error, UpgradeRequest>({
    mutationFn: (request) => subscriptionService.upgrade(request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subscriptionKeys.current() });
      qc.invalidateQueries({ queryKey: subscriptionKeys.billing(1, 10) });
    },
  });
}

/**
 * Cancel a subscription (transitions to 'cancelling' state). The
 * subscription remains usable until endDate.
 */
export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation<
    Subscription,
    Error,
    { id: string; reason?: string; feedback?: string }
  >({
    mutationFn: ({ id, reason, feedback }) =>
      subscriptionService.cancel(id, { reason, feedback }),
    onSuccess: (updated) => {
      qc.setQueryData(subscriptionKeys.current(), updated);
      qc.invalidateQueries({ queryKey: subscriptionKeys.current() });
    },
  });
}

/**
 * Reactivate a 'cancelling' subscription before it terminates.
 */
export function useReactivateSubscription() {
  const qc = useQueryClient();
  return useMutation<Subscription, Error, string>({
    mutationFn: (id) => subscriptionService.reactivate(id),
    onSuccess: (updated) => {
      qc.setQueryData(subscriptionKeys.current(), updated);
      qc.invalidateQueries({ queryKey: subscriptionKeys.current() });
    },
  });
}

/**
 * Renew a 'cancelled' or 'expired' subscription. Re-enables Paystack
 * and resets the billing window.
 */
export function useRenewSubscription() {
  const qc = useQueryClient();
  return useMutation<Subscription, Error, string>({
    mutationFn: (id) => subscriptionService.renew(id),
    onSuccess: (updated) => {
      qc.setQueryData(subscriptionKeys.current(), updated);
      qc.invalidateQueries({ queryKey: subscriptionKeys.current() });
    },
  });
}

/**
 * Toggle auto-renewal.
 */
export function useUpdateAutoRenew() {
  const qc = useQueryClient();
  return useMutation<Subscription, Error, { id: string; autoRenew: boolean }>({
    mutationFn: ({ id, autoRenew }) =>
      subscriptionService.updateSettings(id, { autoRenew }),
    onSuccess: (updated) => {
      qc.setQueryData(subscriptionKeys.current(), updated);
    },
  });
}

/**
 * Update payment method.
 */
export function useUpdatePaymentMethod() {
  const qc = useQueryClient();
  return useMutation<Subscription, Error, PaymentMethodUpdate>({
    mutationFn: (request) => subscriptionService.updatePaymentMethod(request),
    onSuccess: (updated) => {
      qc.setQueryData(subscriptionKeys.current(), updated);
    },
  });
}

/**
 * Convenience: invalidate everything subscription-related. Useful from
 * the payment-callback page after Paystack returns.
 */
export function useInvalidateSubscription() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: subscriptionKeys.all });
}
